<template>
  <div class="settings">
    <h1 class="page-title">{{ $t('customerSettings.title') }}</h1>
    <div v-if="loading" class="loading">{{ $t('customerSettings.loading') }}</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <template v-else>
      <!-- Tabs Navigation -->
      <div class="tabs-navigation">
        <button 
          v-for="tab in tabs" 
          :key="tab.id"
          class="tab-button"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          <i :class="tab.icon"></i>
          {{ $t(`customerSettings.tabs.${tab.id}`) }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Profile Tab -->
        <div v-show="activeTab === 'profile'" class="tab-pane">
          <div class="settings-section">
            <h2>{{ $t('customerSettings.profile.title') }}</h2>
            <div class="profile-form">
              <div class="avatar-section">
                <div class="avatar">
                  <img :src="profile.avatar || '@/assets/avatar.png'" alt="Profile Avatar" />
                  <button class="change-avatar-btn">
                    <i class="fas fa-camera"></i>
                    {{ $t('customerSettings.profile.avatar.changePhoto') }}
                  </button>
                </div>
              </div>
              <div class="form-group">
                <label>{{ $t('customerSettings.profile.form.fullName') }}</label>
                <input type="text" v-model="fullName" :placeholder="$t('customerSettings.profile.form.fullName')">
              </div>
              <div class="form-group">
                <label>{{ $t('customerSettings.profile.form.email') }}</label>
                <input type="email" v-model="profile.email" :placeholder="$t('customerSettings.profile.form.email')">
              </div>
              <div class="form-group">
                <label>{{ $t('customerSettings.profile.form.phoneNumber') }}</label>
                <input type="tel" v-model="profile.phone" :placeholder="$t('customerSettings.profile.form.phoneNumber')">
              </div>
              <button class="save-btn" @click="saveProfile">{{ $t('customerSettings.profile.form.saveChanges') }}</button>
            </div>
          </div>
        </div>

        <!-- Security Tab -->
        <div v-show="activeTab === 'security'" class="tab-pane">
          <div class="settings-section">
            <h2>{{ $t('customerSettings.security.title') }}</h2>
            <div class="security-form">
              <div class="form-group">
                <label>{{ $t('customerSettings.security.form.currentPassword') }}</label>
                <div class="password-input-wrapper">
                  <input 
                    :type="showCurrentPassword ? 'text' : 'password'" 
                    v-model="security.currentPassword" 
                    :placeholder="$t('customerSettings.security.form.currentPassword')"
                  >
                  <i 
                    :class="['password-toggle', showCurrentPassword ? 'fas fa-eye-slash' : 'fas fa-eye']"
                    @click="showCurrentPassword = !showCurrentPassword"
                  ></i>
                </div>
              </div>
              <div class="form-group">
                <label>{{ $t('customerSettings.security.form.newPassword') }}</label>
                <div class="password-input-wrapper">
                  <input 
                    :type="showNewPassword ? 'text' : 'password'" 
                    v-model="security.newPassword" 
                    :placeholder="$t('customerSettings.security.form.newPassword')"
                  >
                  <i 
                    :class="['password-toggle', showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye']"
                    @click="showNewPassword = !showNewPassword"
                  ></i>
                </div>
              </div>
              <div class="form-group">
                <label>{{ $t('customerSettings.security.form.confirmPassword') }}</label>
                <div class="password-input-wrapper">
                  <input 
                    :type="showConfirmPassword ? 'text' : 'password'" 
                    v-model="security.confirmPassword" 
                    :placeholder="$t('customerSettings.security.form.confirmPassword')"
                  >
                  <i 
                    :class="['password-toggle', showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye']"
                    @click="showConfirmPassword = !showConfirmPassword"
                  ></i>
                </div>
              </div>
              <div class="form-actions">
                <p v-if="securityError" class="error-message">{{ securityError }}</p>
                <p v-if="securitySuccess" class="success-message">{{ securitySuccess }}</p>
                <button 
                  class="save-btn" 
                  @click="updatePassword"
                  :disabled="isUpdatingPassword"
                >
                  {{ isUpdatingPassword ? $t('customerSettings.security.form.updating') : $t('customerSettings.security.form.updatePassword') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Notifications Tab -->
        <div v-show="activeTab === 'notifications'" class="tab-pane">
          <div class="settings-section">
            <h2>{{ $t('customerSettings.notifications.title') }}</h2>
            <div class="notification-settings">
              <div class="notification-group">
                <div class="notification-header">
                  <h3>{{ $t('customerSettings.notifications.email.title') }}</h3>
                  <p>{{ $t('customerSettings.notifications.email.subtitle') }}</p>
                </div>
                <div class="notification-options">
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="notifications.updates">
                    {{ $t('customerSettings.notifications.email.updates') }}
                  </label>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="notifications.security">
                    {{ $t('customerSettings.notifications.email.security') }}
                  </label>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="notifications.newsletter">
                    {{ $t('customerSettings.notifications.email.newsletter') }}
                  </label>
                </div>
              </div>

              <div class="notification-group">
                <div class="notification-header">
                  <h3>{{ $t('customerSettings.notifications.push.title') }}</h3>
                  <p>{{ $t('customerSettings.notifications.push.subtitle') }}</p>
                </div>
                <div class="notification-options">
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="notifications.pushUpdates">
                    {{ $t('customerSettings.notifications.push.updates') }}
                  </label>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="notifications.pushSecurity">
                    {{ $t('customerSettings.notifications.push.security') }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Package Tab -->
        <div v-show="activeTab === 'package'" class="tab-pane">
          <div class="settings-section" v-if="packageInfo">
            <h2>{{ $t('customerSettings.package.title') }}</h2>
            <div class="package-form">
              <!-- Package Details Group -->
              <div class="form-group-section">
                <h3>{{ $t('customerSettings.package.sections.packageDetails.title') }}</h3>
                <div class="form-grid">
                  <div class="form-group">
                    <label>{{ $t('customerSettings.package.sections.packageDetails.selectedPackage') }}</label>
                    <input type="text" v-model="packageInfo.selectedPackage" :placeholder="$t('customerSettings.package.sections.packageDetails.selectedPackage')">
                  </div>
                  <div class="form-group">
                    <label>{{ $t('customerSettings.package.sections.packageDetails.packageStatus') }}</label>
                    <select v-model="packageInfo.packageStatus" class="form-select">
                      <option value="">{{ $t('customerSettings.package.sections.packageDetails.status.select') }}</option>
                      <option value="active">{{ $t('customerSettings.package.sections.packageDetails.status.active') }}</option>
                      <option value="pending">{{ $t('customerSettings.package.sections.packageDetails.status.pending') }}</option>
                      <option value="expired">{{ $t('customerSettings.package.sections.packageDetails.status.expired') }}</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label>{{ $t('customerSettings.package.sections.packageDetails.startDate') }}</label>
                    <input type="date" v-model="packageInfo.packageStartDate">
                  </div>
                  <div class="form-group">
                    <label>{{ $t('customerSettings.package.sections.packageDetails.endDate') }}</label>
                    <input type="date" v-model="packageInfo.packageEndDate">
                  </div>
                </div>
              </div>

              <!-- Business Information Group -->
              <div class="form-group-section">
                <h3>{{ $t('customerSettings.package.sections.businessInfo.title') }}</h3>
                <div class="form-grid">
                  <div class="form-group">
                    <label>{{ $t('customerSettings.package.sections.businessInfo.activity') }}</label>
                    <input type="text" v-model="packageInfo.businessActivity" :placeholder="$t('customerSettings.package.sections.businessInfo.activity')">
                  </div>
                  <div class="form-group">
                    <label>{{ $t('customerSettings.package.sections.businessInfo.employeeCount') }}</label>
                    <input type="number" v-model="packageInfo.employeeCount" :placeholder="$t('customerSettings.package.sections.businessInfo.employeeCount')">
                  </div>
                  <div class="form-group">
                    <label>{{ $t('customerSettings.package.sections.businessInfo.progress') }}</label>
                    <select v-model="packageInfo.businessProgress" class="form-select">
                      <option value="">{{ $t('customerSettings.package.sections.businessInfo.progressOptions.select') }}</option>
                      <option value="startup">{{ $t('customerSettings.package.sections.businessInfo.progressOptions.startup') }}</option>
                      <option value="growing">{{ $t('customerSettings.package.sections.businessInfo.progressOptions.growing') }}</option>
                      <option value="established">{{ $t('customerSettings.package.sections.businessInfo.progressOptions.established') }}</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label>{{ $t('customerSettings.package.sections.businessInfo.goals') }}</label>
                    <textarea v-model="packageInfo.businessGoals" :placeholder="$t('customerSettings.package.sections.businessInfo.goals')" rows="3"></textarea>
                  </div>
                </div>
              </div>

              <!-- Responsible Party Information -->
              <div class="form-group-section">
                <h3>{{ $t('customerSettings.package.sections.responsibleParty.title') }}</h3>
                <div class="form-grid">
                  <div class="form-group">
                    <label>{{ $t('customerSettings.package.sections.responsibleParty.name') }}</label>
                    <input type="text" v-model="packageInfo.responsiblePartyName" :placeholder="$t('customerSettings.package.sections.responsibleParty.name')">
                  </div>
                  <div class="form-group">
                    <label>{{ $t('customerSettings.package.sections.responsibleParty.role') }}</label>
                    <input type="text" v-model="packageInfo.role" :placeholder="$t('customerSettings.package.sections.responsibleParty.role')">
                  </div>
                  <div class="form-group">
                    <label>{{ $t('customerSettings.package.sections.responsibleParty.experience') }}</label>
                    <select v-model="packageInfo.businessExperience" class="form-select">
                      <option value="">{{ $t('customerSettings.package.sections.responsibleParty.experienceOptions.select') }}</option>
                      <option value="beginner">{{ $t('customerSettings.package.sections.responsibleParty.experienceOptions.beginner') }}</option>
                      <option value="intermediate">{{ $t('customerSettings.package.sections.responsibleParty.experienceOptions.intermediate') }}</option>
                      <option value="expert">{{ $t('customerSettings.package.sections.responsibleParty.experienceOptions.expert') }}</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label>{{ $t('customerSettings.package.sections.responsibleParty.reason') }}</label>
                    <textarea v-model="packageInfo.reasonForApplying" :placeholder="$t('customerSettings.package.sections.responsibleParty.reason')" rows="3"></textarea>
                  </div>
                </div>
              </div>

              <div class="form-actions">
                <button class="save-btn" @click="savePackageInfo">{{ $t('customerSettings.package.saveButton') }}</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Danger Zone Tab -->
        <div v-show="activeTab === 'danger'" class="tab-pane">
          <div class="settings-section danger-zone">
            <h2>{{ $t('customerSettings.dangerZone.title') }}</h2>
            <div class="danger-actions">
              <div class="danger-action">
                <div>
                  <h3>{{ $t('customerSettings.dangerZone.deleteAccount.title') }}</h3>
                  <p>{{ $t('customerSettings.dangerZone.deleteAccount.description') }}</p>
                </div>
                <button class="delete-btn" @click="confirmDelete">{{ $t('customerSettings.dangerZone.deleteAccount.button') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import '@/styles/Settings.css'
import authService from '@/services/authService'
import axiosInstance from '@/services/axiosInstance'

export default {
  name: 'CustomerSettings',
  props: {
    customerId: {
      type: String,
      required: false
    },
    selectedBusiness: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      activeTab: 'profile',
      tabs: [
        { id: 'profile', name: 'Profile', icon: 'fas fa-user' },
        { id: 'security', name: 'Security', icon: 'fas fa-shield-alt' },
        { id: 'notifications', name: 'Notifications', icon: 'fas fa-bell' },
        { id: 'package', name: 'Package', icon: 'fas fa-box' },
        { id: 'danger', name: 'Danger Zone', icon: 'fas fa-exclamation-triangle' }
      ],
      profile: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        avatar: null
      },
      security: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      securityError: null,
      securitySuccess: null,
      isUpdatingPassword: false,
      notifications: {
        updates: true,
        security: true,
        newsletter: false,
        pushUpdates: true,
        pushSecurity: true
      },
      settings: {
        notifications: {
          email: true,
          sms: false,
          mailAlerts: true
        },
        preferences: {
          language: 'en',
          timezone: 'UTC',
          dateFormat: 'MM/DD/YYYY'
        },
        security: {
          twoFactorAuth: false,
          sessionTimeout: 30
        }
      },
      loading: false,
      error: null,
      success: null,
      packageInfo: {
        selectedPackage: '',
        packageStatus: '',
        packageStartDate: '',
        packageEndDate: '',
        responsiblePartyName: '',
        role: '',
        businessActivity: '',
        reasonForApplying: '',
        businessExperience: '',
        employeeCount: '',
        businessProgress: '',
        businessGoals: ''
      },
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false
    }
  },
  computed: {
    getCustomerId() {
      return this.customerId || localStorage.getItem('customerId')
    },
    fullName() {
      return `${this.profile.firstName} ${this.profile.lastName}`
    }
  },
  created() {
    // Initialize auth state
    authService.initAuth()
    
    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      this.error = 'Please log in to access your profile'
      return
    }
  },
  mounted() {
    // Try to initialize packageInfo from selectedBusiness if available
    let business = this.selectedBusiness;
    if (!business) {
      // Try to get from localStorage if not passed as prop
      try {
        const stored = localStorage.getItem('selectedBusiness');
        if (stored) business = JSON.parse(stored);
      } catch (e) {
        console.warn('Failed to parse selectedBusiness from localStorage:', e);
      }
    }
    if (business) {
      this.packageInfo = {
        selectedPackage: business.selectedPackage || '',
        packageStatus: business.packageStatus || '',
        packageStartDate: business.packageStartDate || '',
        packageEndDate: business.packageEndDate || '',
        responsiblePartyName: business.responsiblePartyName || '',
        role: business.role || '',
        businessActivity: business.businessActivity || '',
        reasonForApplying: business.reasonForApplying || '',
        businessExperience: business.businessExperience || '',
        employeeCount: business.employeeCount || '',
        businessProgress: business.businessProgress || '',
        businessGoals: business.businessGoals || ''
      };
    }
  },
  watch: {
    getCustomerId: {
      immediate: true,
      async handler(newCustomerId) {
        if (newCustomerId && authService.isAuthenticated()) {
          await this.loadCustomerData()
        } else if (!authService.isAuthenticated()) {
          this.error = 'Please log in to access your profile'
        } else {
          this.error = 'No customer ID found'
        }
      }
    }
  },
  methods: {
    async loadCustomerData() {
      if (!authService.isAuthenticated()) {
        this.error = 'Please log in to access your profile'
        return
      }

      this.loading = true
      this.error = null
      try {
        const response = await authService.getProfile()
        if (response.success && response.data) {
          this.profile = {
            firstName: response.data.firstName,
            lastName: response.data.lastName,
            email: response.data.email,
            phone: response.data.phone,
            avatar: response.data.avatar
          }
        } else {
          throw new Error(response.message || 'Failed to load profile data')
        }
      } catch (error) {
        console.error('Error loading customer data:', error)
        this.error = error.message || 'Failed to load customer data. Please try again.'
      } finally {
        this.loading = false
      }
    },
    async saveProfile() {
      if (!authService.isAuthenticated()) {
        this.error = 'Please log in to update your profile'
        return
      }

      this.loading = true
      this.error = null
      this.success = null
      try {
        const profileData = {
          firstName: this.profile.firstName,
          lastName: this.profile.lastName,
          phone: this.profile.phone
        }
        const response = await authService.updateProfile(profileData)
        if (response.success) {
          this.success = 'Profile updated successfully!'
        } else {
          throw new Error(response.message || 'Failed to update profile')
        }
      } catch (error) {
        console.error('Error saving profile:', error)
        this.error = error.message || 'Failed to save profile. Please try again.'
      } finally {
        this.loading = false
      }
    },
    async saveSettings() {
      if (!authService.isAuthenticated()) {
        this.error = 'Please log in to update your settings'
        return
      }

      this.loading = true
      this.error = null
      this.success = null
      try {
        // TODO: Replace with actual API call
        // await settingsService.updateSettings(this.getCustomerId, this.settings)
        this.success = 'Settings saved successfully!'
      } catch (error) {
        console.error('Error saving settings:', error)
        this.error = 'Failed to save settings. Please try again.'
      } finally {
        this.loading = false
      }
    },
    confirmDelete() {
      // Handle account deletion confirmation
      console.log('Confirming account deletion')
    },
    async savePackageInfo() {
      // TODO: Implement actual save logic (API call)
      this.success = 'Package info saved!';
    },
    async updatePassword() {
      // Reset messages
      this.securityError = null;
      this.securitySuccess = null;

      // Validate inputs
      if (!this.security.currentPassword || !this.security.newPassword || !this.security.confirmPassword) {
        this.securityError = this.$t('customerSettings.security.errors.required');
        return;
      }

      if (this.security.newPassword !== this.security.confirmPassword) {
        this.securityError = this.$t('customerSettings.security.errors.mismatch');
        return;
      }

      if (this.security.newPassword.length < 8) {
        this.securityError = this.$t('customerSettings.security.errors.tooShort');
        return;
      }

      try {
        this.isUpdatingPassword = true;
        const passwordData = {
          currentPassword: this.security.currentPassword,
          newPassword: this.security.newPassword
        };
        const token = localStorage.getItem('token');
        const response = await axiosInstance.put(
          '/customers/update-password',
          passwordData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.success) {
          this.securitySuccess = this.$t('customerSettings.security.success');
          this.security.currentPassword = '';
          this.security.newPassword = '';
          this.security.confirmPassword = '';
          setTimeout(() => { this.securitySuccess = null; }, 3000);
        } else {
          this.securityError = response.data.message || this.$t('customerSettings.security.errors.required');
        }
      } catch (error) {
        console.error('Error updating password:', error);
        if (error.response && error.response.data) {
          this.securityError = error.response.data.message || this.$t('customerSettings.security.errors.required');
        } else if (error.message) {
          this.securityError = error.message;
        } else {
          this.securityError = this.$t('customerSettings.security.errors.required');
        }
      } finally {
        this.isUpdatingPassword = false;
      }
    }
  }
}
</script>

// Style block removed. All styles will be moved to CustomerSettings.css.